import React, { useState, useEffect } from "react";
import Select from "react-select";
import { useField, useFormikContext } from "formik";

const AsyncFormikDropdown = ({
    label,
    name,
    apiCallFunction, // Function to fetch data based on input
    placeholder = "Select",
    required = false,
    needSearch = false,
    debounceTime = 300, // Debounce for API calls
    maxMenuHeight = 200, // Maximum height for dropdown menu
    initialOptions = [], // Pre-seed options (e.g. for default selected value display)
    formatOptionLabel, // react-select: custom option (e.g. thumbnail + name)
    isDisabled = false,
    ...props
}) => {
    const { setFieldValue } = useFormikContext();
    const [field, meta] = useField(name);

    const [inputValue, setInputValue] = useState(""); // Tracks user's input
    const [options, setOptions] = useState(initialOptions || []); // Stores dropdown options
    const [isLoading, setIsLoading] = useState(false); // For loader while fetching

    const initialOptionsKey =
        initialOptions?.map((o) => `${o.value}::${o.label}`).join("|") || "";

    useEffect(() => {
        if (!initialOptions?.length) return;
        setOptions((prev) => {
            const map = new Map();
            initialOptions.forEach((o) => map.set(String(o.value), o));
            prev.forEach((o) => {
                if (!map.has(String(o.value))) map.set(String(o.value), o);
            });
            return Array.from(map.values());
        });
    }, [initialOptionsKey]);

    // Function to handle debounce for input changes
    useEffect(() => {
        const delayDebounce = setTimeout(() => {
            if (inputValue && apiCallFunction) {
                fetchOptions(inputValue);
            }
        }, debounceTime);

        return () => clearTimeout(delayDebounce); // Cleanup timeout
    }, [inputValue]);

    // Function to fetch options via API
    const fetchOptions = async (input) => {
        try {
            setIsLoading(true);
            const response = await apiCallFunction(input); // Call the provided function
            if (Array.isArray(response)) {
                setOptions(response);
            } else {
                setOptions([]);
            }
        } catch (error) {
            console.error("Error fetching options:", error);
            setOptions([]);
        } finally {
            setIsLoading(false);
        }
    };

    const handleChange = (selectedOption) => {
        setFieldValue(name, selectedOption ? selectedOption.value : "");
        if (props.onChange) {
            props.onChange(selectedOption); // Call the parent onChange if provided
        }
    };

    return (
        <div className="form-control w-full">
            {/* Label */}
            {label && (
                <label htmlFor={name} className="block text-sm font-medium text-gray-700 mb-1">
                    {label}
                    {required && <span className="text-red-500 ml-1">*</span>}
                </label>
            )}
            <Select
                options={options}
                value={
                    options.find(
                        (option) => String(option.value) === String(field.value ?? "")
                    ) || null
                }
                onChange={handleChange}
                onInputChange={(value) => setInputValue(value)} // Update inputValue on user typing
                classNamePrefix="react-select"
                placeholder={placeholder}
                isSearchable={needSearch}
                isLoading={isLoading} // Loader for async calls
                formatOptionLabel={formatOptionLabel}
                isDisabled={isDisabled}
                noOptionsMessage={() => "No options available"}
                maxMenuHeight={maxMenuHeight} // Restrict dropdown height
                menuPlacement="auto"
                menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                styles={{
                    control: (base, state) => ({
                        ...base,
                        minHeight: '44px',
                        borderColor: meta.touched && meta.error ? '#ef4444' : state.isFocused ? '#2563eb' : '#d1d5db',
                        boxShadow: meta.touched && meta.error ? '0 0 0 2px rgba(239, 68, 68, 0.1)' : state.isFocused ? '0 0 0 2px rgba(37, 99, 235, 0.1)' : 'none',
                        '&:hover': {
                            borderColor: meta.touched && meta.error ? '#dc2626' : '#9ca3af'
                        },
                        borderRadius: '8px',
                        transition: 'all 0.2s ease'
                    }),
                    menu: (base) => ({
                        ...base,
                        borderRadius: '8px',
                        boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
                        border: '1px solid #e5e7eb',
                        maxHeight: maxMenuHeight,
                        overflow: 'hidden'
                    }),
                    menuList: (base) => ({
                        ...base,
                        maxHeight: maxMenuHeight,
                        overflowY: 'auto',
                        scrollbarWidth: 'thin',
                        scrollbarColor: '#d1d5db #f9fafb',
                        '&::-webkit-scrollbar': {
                            width: '6px'
                        },
                        '&::-webkit-scrollbar-track': {
                            backgroundColor: '#f9fafb',
                            borderRadius: '3px'
                        },
                        '&::-webkit-scrollbar-thumb': {
                            backgroundColor: '#d1d5db',
                            borderRadius: '3px',
                            '&:hover': {
                                backgroundColor: '#9ca3af'
                            }
                        }
                    }),
                    option: (base, state) => ({
                        ...base,
                        backgroundColor: state.isSelected ? '#2563eb' : state.isFocused ? '#f3f4f6' : 'white',
                        color: state.isSelected ? 'white' : '#374151',
                        '&:active': {
                            backgroundColor: '#1d4ed8'
                        }
                    }),
                    menuPortal: (base) => ({
                        ...base,
                        zIndex: 9999
                    })
                }}
            />

            {/* Error Message */}
            {meta.touched && meta.error && (
                <p
                    id={`${name}-error`}
                    className="mt-1 text-sm text-red-600 flex items-center gap-1"
                    role="alert"
                >
                    <svg className="w-4 h-4 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                    {meta.error}
                </p>
            )}
        </div>
    );
};

export default AsyncFormikDropdown;
